import { useMemo } from "react";

import { BannerCarousel } from "../carousels";
import { useSSOMarketingPopupContext } from "../popup/PopupProvider";
import { classNames } from "../helpers";
import { isMobilePreviewMode } from "./utils";
import { CarouselOptions } from "../@shadcn/components/ui/carousel";

const OPTIONS: CarouselOptions = {
  loop: true,
  align: "center",
};

export const PopupCarousel = () => {
  const {
    overRides,
    popup: { card, backgroundColor: mainBackgroundColor },
  } = useSSOMarketingPopupContext();

  // TODO: Add logic to check if the card is enabled
  const isCardEnabled = card.enabled;

  if (!isCardEnabled) {
    return null;
  }

  const {
    backgroundColor: commonBackgroundColor,
    backgroundImage: commonBackgroundImage,
    textColor,
    borderColor: commonBorderColor,
    borderWidth: commonBorderWidth,
    borderRadius: commonBorderRadius,
  } = card;
  const slides = useMemo(() => {
    // Create initial slides array from card items
    const initialSlides = card.items.map((item, index) => {
      const {
        isTopIconTextOrImage,
        topIconText,
        topIconImage,
        topIconTextColor,
        title,
        titleColor,
        description,
        descriptionColor,
      } = item;
      const isTopIconText = isTopIconTextOrImage === "text" && topIconText;
      const isTopIconImage = isTopIconTextOrImage === "image";

      const borderColor = item.borderColor || commonBorderColor;
      const borderWidth = item.borderWidth || commonBorderWidth;
      const borderRadius = item.borderRadius || commonBorderRadius || "8px";

      const backgroundColor = item.backgroundColor || commonBackgroundColor;
      const backgroundImage = item.backgroundImage || commonBackgroundImage;
      const backgroundImageFit = item.backgroundImageFit;
      const fit = (backgroundImageFit || "cover") as "cover" | "contain" | "fill";
      const backgroundImageWidth =
        fit === "cover" || fit === "fill"
          ? "100%"
          : "auto";

      return (
        <div
          style={{
            backgroundColor,
            color: textColor,
            borderColor,
            borderWidth,
            borderRadius,
          }}
          className={classNames(
            "!ui-w-full ui-flex ui-flex-col ui-items-center xxs:ui-h-[155px] xxs:ui-w-[292px] ui-justify-center ui-relative ui-z-10 ui-text-center ui-px-6 ui-py-5 md:ui-py-8 ui-overflow-hidden",
            item.topIconText ? "ui-space-y-2" : "",
            isMobilePreviewMode(overRides) ? "ui-h-[120px]" : "ui-h-[155px] md:ui-h-[189px]"
          )}
          key={index}
        >
          {backgroundImage && (
            <img
              src={backgroundImage}
              style={{
                width: backgroundImageWidth,
                objectFit: fit,
              }}
              className="ui-h-full ui-absolute ui-z-[-1] ui-top-0 ui-left-1/2 -translate-x-1/2"
            />
          )}

          {backgroundImage && (
            <div
              className="ui-h-full ui-w-full ui-absolute ui-top-0 ui-z-[-2] ui-left-1/2 -translate-x-1/2"
            >
              <img
                className="ui-h-full ui-w-full object-cover ui-scale-[1.5] ui-blur-2xl ui-opacity-[0.6]"
                src={backgroundImage}
                alt=""
                aria-hidden="true"
              />
            </div>
          )}

          {isTopIconText && (
            <p
              className={classNames(
                "ui-font-semibold",
                isMobilePreviewMode(overRides)
                  ? "ui-text-[20px]"
                  : "ui-text-[30px]",
              )}
              style={{ color: topIconTextColor || textColor }}
            >
              {topIconText}
            </p>
          )}
          {isTopIconImage && (
            <img
              src={topIconImage}
              alt={topIconText}
              className="ui-max-w-14 ui-max-h-14 ui-pb-1"
            />
          )}
          <h3
            className="ui-text-sm ui-font-semibold"
            style={{ color: titleColor || textColor }}
          >
            {title}
          </h3>
          {description && (
            <p
              className="ui-text-xs ui-font-medium ui-pt-2"
              style={{ color: descriptionColor || textColor }}
              dangerouslySetInnerHTML={{ __html: item.description ?? "" }}
            />
          )}
        </div>
      );
    });

    // If there are exactly 2 slides, duplicate them to create 4
    if (initialSlides.length === 2) {
      return [...initialSlides, ...initialSlides];
    }

    return initialSlides;
  }, [card]);

  return (
    <BannerCarousel
      slides={slides}
      options={OPTIONS}
      styles={{
        button: { backgroundColor: mainBackgroundColor },
        card: { borderRadius: commonBorderRadius },
      }}
      overRides={overRides}
    />
  );
};
